// React
import React, { Component } from "react";

// Libraries
import Numeral from 'numeral';

// Panel
import { PanelHeading, PanelBody, Dropdown } from '../../Components';
import { Holding } from './';

// Styles
import "./dashboard.css";

// Stores 
import { UserProfileStore, SampleDataStore, CurrencyStore } from '../../Stores';

/**********************************************************
 * 
 *  Overview shows the users total holdings per exchange with pnl
 *  
 **********************************************************/

class Overview extends Component {

/**********************************************************
 *  Constructor
 **********************************************************/
    constructor(props) {

        super(props);

        this.state = {
            data : {
                'BTC' : {},
                'USD' : {}
            },
            description : ['All of your holdings aggregated by exchange with a summary at bottom.', 'Profit and loss is calculated in terms of both USD & BTC and shown as a value and percentage.', 'Expand each exchange to see your holdings.'],
            quoteCurrency : 'USD',
            timeframe : 'Today',
            loading : true,
            message : null,
            showHoldings : []
        };

    }


/**********************************************************
 *  Component Lifecycle Functions
 **********************************************************/

    // After first render, fetch all parameters and the corresponding chart
    componentDidMount() {

        //console.log('Overview.componentDidMount()');

        // If user logged in, listen for updates
        // and fetch the overview data
        if(this.props.user !== null) {
            this.listenForUpdates();
            this.getOverview();
        }
        else {
            this.getSampleOverview();
        }

    }

/**********************************************************
 *  Helper Functions
 **********************************************************/

    // Retrieves the overview data from UserProfileStore
    getOverview() {
        
        //console.log('Overview.getOverview()');

        const overview = UserProfileStore.getOverview();

        if(overview !== null) {

            this.setState((prev, props) => {
                return {
                    loading : false,
                    data : overview,
                    message : null
                }
            });

        }

    }


    // Gets and displays the sample overview data
    getSampleOverview() {

        this.setState((prev, props) => {
            return {
                loading : false,
                data : SampleDataStore.getOverviewData()
            }
        });

    }


    // Listens for updates to pnl
    listenForUpdates() {

        var emitter = UserProfileStore.getEmitter();

        // Listen for pnl updates
        emitter.addListener('pnl', (updated) => {

            if(updated) {
                this.getOverview();
            }
            else {

                var error = UserProfileStore.getPnlError();

                var msg = {};

                // Graceful catch for users that havent added any api keys
                if(Object.keys(error).length > 0 && (error.message.includes("No API Keys available for user") || error.message.includes('No EOD object for user'))) {
                    msg = {
                        title : 'No Api Keys Available', 
                        details : 'Check out the Api Keys page to add access to your exchanges.',
                        type : ''
                    }
                }
                else {
                    msg = {
                        title : 'Uh Oh..',
                        details : 'Something broke on our end. Try refreshing the panel or contacting us.',
                        type : 'warning'
                    };
                }

                this.setState((prev, props) => {
                    return {
                        loading : false,
                        message : msg
                    }
                });
            }

        });

        // Listen for loading indicator
        emitter.addListener('loadingPnl', (loading) => {
            
            if(loading) {
                this.setState((prev, props) => {
                    return {
                        loading : true
                    }
                });
            }

        });

    }


    // Returns the aggregate pnl from all exchanges
    buildTotalPnl() {

        // For Totals Row
        var total = 0.0;
        var pnl = 0.0;

        const exchanges = this.state.data[this.state.quoteCurrency];

        const pnlTimeframe = this.state.timeframe === 'Today' ? 'dailyPnl' : 'totalPnl';
        const deltaTimeframe = this.state.timeframe === 'Today' ? 'dailyChange' : 'totalChange';

        for(let [exchange, details] of Object.entries(exchanges)) {

            total += parseFloat(details.position);
            pnl += parseFloat(details[pnlTimeframe]);
        }

        // Calculate Weighted Average
        var weightedAverage = 0.0;

        for(let [exchange, details] of Object.entries(exchanges)) {
            weightedAverage += ((details.position/total) * details[deltaTimeframe]);
        }

        return {
            'pnlChange' : weightedAverage*100,
            'total' : total,
            'pnl' : pnl
        }

    }


    // Refreshes the pnl
    refreshPnl = () => {

        if(this.props.user !== null) {
            UserProfileStore.getPnl(true);
        }

    }


    // Selecing a quote currency will update the dropdown,
    // decimal formatting and the currency symbol
    selectQuoteCurrency(currency) {

        this.setState((prev, props) => {
            return {
                quoteCurrency : currency
            }
        });

    }


    // Update the timeframe between Today and All-time
    selectTimeframe(timeframe) {

        this.setState((prev, props) => {
            return {
                timeframe : timeframe
            }
        });

    }

    
    loadHoldings = (exchange) => {

        var showHoldings = this.state.showHoldings;
        var index = showHoldings.indexOf(exchange);

        if(index > -1)
            showHoldings.splice(index, 1);
        else
            showHoldings.push(exchange);

        this.setState((prev, props) => {
            return {
                showHoldings : showHoldings
            }
        });

    }

/**********************************************************
 *  UI Building Functions
 **********************************************************/

    // Builds a row panel for each exchange's overview
    buildOverviews = () => {

        const exchanges = this.state.data[this.state.quoteCurrency];

        const timePnl = this.state.timeframe === 'Today' ? 'dailyPnl' : 'totalPnl';

        const timeChange = this.state.timeframe === 'Today' ? 'dailyChange' : 'totalChange';

        return (

            Object.keys(exchanges).sort().map( (exchange) => {
                                
                return (
                    <React.Fragment key={exchange}>
                    <div className="flex-panel" style={{paddingLeft:'28px', position:'relative'}}>

                        <div className='overview-menu' onClick={() => this.loadHoldings(exchange)}>
                            {
                                this.state.showHoldings.includes(exchange) ? (<i className='icon-menu-close'></i>) : 
                                (
                                    <div className='circles'>
                                        <div></div>
                                        <div></div>
                                        <div></div>
                                    </div>
                                )
                            }
                        </div>

                        <div className="child-row">

                            <div className="row-box left">

                                <div>
                                    { exchange }
                                </div>

                            </div>

                            <div className="row-box right">

                                <div>
                                    <span className="right-pad">{ CurrencyStore.getCurrencySymbol(this.state.quoteCurrency) }</span>
                                    { Numeral(exchanges[exchange][timePnl]).format(CurrencyStore.getFormat(this.state.quoteCurrency)) }
                                    
                                    <i className={exchanges[exchange][timePnl] > 0 ? 'icon-arrow-up8 green-text left-pad arrow hover-up' : 'icon-arrow-down8 red-text left-pad arrow hover-down'}></i>
                                </div>

                                <div className="lowlight">
                                    { Numeral(exchanges[exchange][timeChange] * 100).format(CurrencyStore.getCurrencyFormat()) } 
                                    <span className="left-pad">%</span>
                                </div>

                            </div>

                            <div className="row-box right">

                                <div>
                                    Total
                                </div>

                                <div>
                                    <span className="right-pad">{ CurrencyStore.getCurrencySymbol(this.state.quoteCurrency) }</span>
                                    { Numeral(exchanges[exchange].position).format(CurrencyStore.getFormat(this.state.quoteCurrency)) }  
                                </div>

                            </div>

                        </div>

                    </div>
                    
                    { exchanges[exchange].holdings !== undefined ? this.buildHoldings(exchanges[exchange].holdings, this.state.showHoldings.includes(exchange)) : null }

                    </React.Fragment>
                    
                );

            })

        );

    }


    // Returns the expandable holdings portion of an exchange overview
    buildHoldings = (holdingsObj, show) => {

        const quoteCurrency = this.state.quoteCurrency;
        const timePnl = this.state.timeframe === 'Today' ? 'dailyPnl' : 'totalPnl';
        const timeChange = this.state.timeframe === 'Today' ? 'dailyChange' : 'totalChange';

        if(show) {

            // convert to array
            var holdings = [];
            for(let[name, details] of Object.entries(holdingsObj))
                holdings.push(details);

            return (
                <div className='exchange-holdings'>
                    {
                        holdings.map( (holding) => {
                            
                            return (
                                <Holding key={holding.ticker} name={holding.name} ticker={holding.ticker} quoteCurrency={quoteCurrency} pnl={holding[timePnl]} change={holding[timeChange]} position={holding.position} quantity={holding.quantity} light={true}/>
                            )
                        })
                    }
                </div>
            )
        }
        else {
            return null;
        }

    }


    // Returns the overview total row
    buildOverviewTotal = () => {

        const total = this.buildTotalPnl();

        return (

            <div className="flex-panel">

                <div className="child-row">

                    <div className="row-box left">

                        <div>
                            Summary
                        </div>

                    </div>

                    <div className="row-box right">

                        <div>
                            <span className="right-pad">{ CurrencyStore.getCurrencySymbol(this.state.quoteCurrency) }</span>
                            { Numeral(total.pnl).format(CurrencyStore.getFormat(this.state.quoteCurrency)) }
                            <i className={total.pnlChange > 0 ? 'icon-arrow-up8 green-text left-pad arrow hover-up' : 'icon-arrow-down8 red-text left-pad arrow hover-down'}></i>
                        </div>

                        <div className="lowlight">
                            { Numeral(total.pnlChange).format(CurrencyStore.getCurrencyFormat()) }    
                            <span className="left-pad">%</span>
                        </div>

                    </div>

                    <div className="row-box right">

                        <div>
                            Total
                        </div>
                        
                        <div>
                            <span className="right-pad">{ CurrencyStore.getCurrencySymbol(this.state.quoteCurrency) }</span>
                            { Numeral(total.total).format(CurrencyStore.getFormat(this.state.quoteCurrency))}
                        </div>

                    </div>

                </div>

            </div>
        );

    }


/**********************************************************
 *  Render
 **********************************************************/

  render() {

    return (

        <div className="panel panel-flat bg-theme-700">

            <PanelHeading title={"Overview"} description={this.state.description} sample={this.props.user === null ? true : false} hiddenMenu={true} refresh={this.props.user === null ? null : this.refreshPnl }>

                <Dropdown icon={this.state.timeframe === 'Today' ? 'icon-calendar52' : 'icon-calendar'} value={this.state.timeframe}>
                    <li onClick={() => this.selectTimeframe('Today')}><a href={null}><i className='icon-calendar52'></i>Today</a></li>
                    <li onClick={() => this.selectTimeframe('Since Signup')}><a href={null}><i className='icon-calendar'></i>Since Signup</a></li>
                </Dropdown>

                <Dropdown icon='icon-coins' value={this.state.quoteCurrency}>
                {
                    Object.keys(this.state.data).sort().map((currency) => {
                        return (
                            <li key={currency} onClick={() => this.selectQuoteCurrency(currency)}><a href={null}><div className='currency-symbol'>{CurrencyStore.getCurrencySymbol(currency)}</div>{currency}</a></li>
                        )
                    })
                }
                </Dropdown>

            </PanelHeading>

            <PanelBody loading={this.state.loading} message={this.state.message}>

                { this.buildOverviews() }

                { this.buildOverviewTotal() }
                    
            </PanelBody>

        </div>

    );
  }
}

export default Overview;





// WEBPACK FOOTER //
// ./src/Views/Dashboard/Overview.js